import * as api from "../api";
import { useDropzone } from "react-dropzone";
import { useQueryClient, useMutation } from "@tanstack/react-query";

export const useDeleteBrief = () => {
  const queryClient = useQueryClient();

  const deleteBrief = async ({ id }) => {
    await api.deleted(`/briefs/${id}`);
    queryClient.invalidateQueries({ queryKey: ["briefs"] });
  };

  return { deleteBrief };
};

export const useGetBrief = () => {
  const fetchBrief = async (id) => {
    const data = await api.get(`/briefs/${id}`);
    return data;
  };

  return { fetchBrief };
};

export const useGetBriefs = () => {
  const queryClient = useQueryClient();

  const refetchBriefs = () => {
    queryClient.invalidateQueries({ queryKey: ["briefs"] });
  };

  return { refetchBriefs };
};

export const useCreateBrief = () => {
  const { refetchBriefs } = useGetBriefs();

  const createBrief = async ({
    court,
    judge,
    files,
    case_name,
    client_number,
    matter_number,
    document_type,
  }) => {
    const file = files[0];
    const { name: filename } = file;

    const { url, fields, brief_id } = await api.post("/briefs", {
      body: JSON.stringify({
        court,
        judge,
        filename,
        case_name,
        client_number,
        matter_number,
        document_type,
      }),
    });

    const formData = new FormData();

    Object.entries(fields).forEach((entry) => {
      const [key, value] = entry;
      formData.append(key, value);
    });

    formData.append("file", file);

    await api.file(url, { method: "POST", body: formData });

    refetchBriefs();

    return { brief_id };
  };

  const uploadBrief = async ({
    brief_id,
    files,
    case_name,
    client_number,
    matter_number,
    document_type,
  }) => {
    const file = files[0];
    const { name: filename } = file;

    const { url, fields } = await api.post("/briefs", {
      body: JSON.stringify({
        brief_id,
        filename,
        case_name,
        client_number,
        matter_number,
        document_type,
      }),
    });

    const formData = new FormData();

    Object.entries(fields).forEach((entry) => {
      const [key, value] = entry;
      formData.append(key, value);
    });

    formData.append("file", file);

    await api.file(url, { method: "POST", body: formData });

    return { brief_id };
  };

  return {
    createBrief,
    uploadBrief,
  };
};

export const useUploadBriefFile = ({ onDrop }) => {
  const { acceptedFiles, getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
  });

  return { acceptedFiles, getRootProps, getInputProps, open };
};

export const useUpdateBrief = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) =>
      api.patch(`/briefs`, { body: JSON.stringify(payload) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
  });

  return mutation;
};
